import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { ArrowLeft, Edit, Plus, Printer, Save, Trash2, User } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import secureStorage from '../utils/secureStorage';
import { writeSanitizedHTML } from '../utils/safePrint';

const API = `${API_BASE_URL}/api`;

const ICS225Form = ({ currentExercise, theme }) => {
  const [view, setView] = useState('list');
  const [ratings, setRatings] = useState([]);
  const [editingRating, setEditingRating] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    incident_name: '',
    incident_number: '',
    home_unit_name_address: '',
    incident_agency_address: '',
    position_held: '',
    date_from: '',
    date_to: '',
    incident_complexity: '',
    incident_definition: '',
    // Rating factors (11-23)
    knowledge_job_competence: '',
    ability_obtain_performance: '',
    planning_preparedness: '',
    using_resources: '',
    adaptability_attitude: '',
    communication_skills: '',
    ability_work_team: '',
    consideration_personnel_welfare: '',
    directing_others: '',
    judgment_decisions_stress: '',
    initiative: '',
    physical_ability_job: '',
    adherence_safety: '',
    remarks: '',
    rated_individual_name: '',
    rated_individual_signature: '',
    rated_individual_date: '',
    rated_by_name: '',
    rated_by_signature: '',
    rated_by_date: ''
  });

  const API = API_BASE_URL || 'http://localhost:8001';

  // Fetch personnel performance ratings
  useEffect(() => {
    const fetchRatings = async () => {
      if (currentExercise?.id) {
        try {
          const response = await axios.get(`${API}/api/personnel-performance-ratings?exercise_id=${currentExercise.id}`);
          setRatings(response.data);
        } catch (error) {
          console.error('Error fetching personnel performance ratings:', error);
        }
      }
    };
    fetchRatings();
  }, [currentExercise, API]);

  // Fetch participants assigned to the exercise (for Name dropdown)
  useEffect(() => {
    const fetchParticipants = async () => {
      if (currentExercise?.id) {
        try {
          const token = secureStorage.getToken();
          const headers = token ? { Authorization: `Bearer ${token}` } : {};
          const response = await axios.get(`${API}/api/exercises/${currentExercise.id}/participants`, { headers });
          const sorted = [...(response.data || [])].sort((a, b) => {
            const an = (a.name || `${a.firstName || ''} ${a.lastName || ''}`).trim().toLowerCase();
            const bn = (b.name || `${b.firstName || ''} ${b.lastName || ''}`).trim().toLowerCase();
            return an.localeCompare(bn);
          });
          setParticipants(sorted);
        } catch (error) {
          console.error('Error fetching exercise participants:', error);
        }
      }
    };
    fetchParticipants();
  }, [currentExercise, API]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const resetForm = () => {
    setFormData({
      name: '',
      incident_name: currentExercise?.exercise_name || '',
      incident_number: '',
      home_unit_name_address: '',
      incident_agency_address: '',
      position_held: '',
      date_from: '',
      date_to: '',
      incident_complexity: '',
      incident_definition: '',
      knowledge_job_competence: '',
      ability_obtain_performance: '',
      planning_preparedness: '',
      using_resources: '',
      adaptability_attitude: '',
      communication_skills: '',
      ability_work_team: '',
      consideration_personnel_welfare: '',
      directing_others: '',
      judgment_decisions_stress: '',
      initiative: '',
      physical_ability_job: '',
      adherence_safety: '',
      remarks: '',
      rated_individual_name: '',
      rated_individual_signature: '',
      rated_individual_date: '',
      rated_by_name: '',
      rated_by_signature: '',
      rated_by_date: ''
    });
  };

  const handleSave = async () => {
    const ratingData = {
      id: editingRating ? editingRating.id : Date.now(),
      ...formData,
      exercise_id: currentExercise?.id,
      created_at: editingRating ? editingRating.created_at : new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    try {
      if (editingRating) {
        await axios.put(`${API}/api/personnel-performance-ratings/${editingRating.id}`, ratingData);
        setRatings(prev => prev.map(item => item.id === editingRating.id ? ratingData : item));
      } else {
        const response = await axios.post(`${API}/api/personnel-performance-ratings`, ratingData);
        setRatings(prev => [...prev, response.data]);
      }
      setView('list');
      setEditingRating(null);
      resetForm();
    } catch (error) {
      console.error('Error saving personnel performance rating:', error);
    }
  };

  const handleEdit = (rating) => {
    setEditingRating(rating);
    setFormData(rating);
    setView('add');
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/api/personnel-performance-ratings/${id}`);
      setRatings(prev => prev.filter(item => item.id !== id));
    } catch (error) {
      console.error('Error deleting personnel performance rating:', error);
    }
  };

  const printICS225 = (ratingArg) => {
    // Priority: explicit arg (e.g., from list click) > current formData when in form > editingRating > first rating
    const ratingToPrint = ratingArg
      || (view === 'add' ? formData : null)
      || editingRating
      || (ratings.length > 0 ? ratings[0] : formData);

    if (!ratingToPrint || (!ratingToPrint.name && !ratingToPrint.incident_name && !ratingToPrint.id)) {
      alert('No personnel performance rating data to print. Please create or select a rating first.');
      return;
    }

    // Renders a filled or empty circle (radio-button style) for print clarity.
    // Uses CSS class (not inline style) because DOMPurify strips style attributes.
    const radioMark = (isSelected) =>
      `<span class="${isSelected ? 'rb rb-on' : 'rb'}"></span>`;
    const printContent = `
      <html>
        <head>
          <title>ICS 225 - Personnel Performance Rating</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; font-size: 12px; }
            .header { text-align: center; margin-bottom: 20px; }
            .form-section { margin-bottom: 15px; }
            .form-row { display: flex; gap: 20px; margin-bottom: 8px; align-items: flex-start; }
            .form-field { flex: 1; }
            .form-field-small { flex: 0.5; }
            .label { font-weight: bold; margin-bottom: 3px; font-size: 11px; }
            .value { border-bottom: 1px solid #000; min-height: 18px; padding: 2px; font-size: 11px; }
            .textarea-value { border: 1px solid #000; min-height: 40px; padding: 5px; font-size: 11px; }
            .section-title { color: #000; font-weight: bold; font-size: 14px; margin: 15px 0 10px 0; }
            .rating-table { width: 100%; border-collapse: collapse; margin: 10px 0; }
            .rating-table th, .rating-table td { border: 1px solid #000; padding: 6px; text-align: center; font-size: 10px; }
            .rating-table th { background-color: #f5f5f5; font-weight: bold; }
            .rating-factor { text-align: left; font-weight: bold; }
            .factor-description { font-size: 9px; font-style: italic; margin-top: 2px; }
            .complexity-options { display: flex; gap: 15px; margin-top: 5px; }
            .complexity-option { display: flex; align-items: center; gap: 5px; }
            .rb { display: inline-block; width: 12px; height: 12px; border: 1.5px solid #000; border-radius: 50%; vertical-align: middle; background: transparent; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            .rb-on { background: #000 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            @media print {
              .rb, .rb-on { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>ICS 225 - INCIDENT PERSONNEL PERFORMANCE RATING</h1>
          </div>
          
          <div class="form-section">
            <div class="form-row">
              <div class="form-field">
                <div class="label">1. NAME:</div>
                <div class="value">${ratingToPrint.name || ''}</div>
              </div>
              <div class="form-field">
                <div class="label">2. INCIDENT NAME:</div>
                <div class="value">${ratingToPrint.incident_name || ''}</div>
              </div>
              <div class="form-field-small">
                <div class="label">3. INCIDENT NUMBER:</div>
                <div class="value">${ratingToPrint.incident_number || ''}</div>
              </div>
            </div>
          </div>

          <div class="form-section">
            <div class="form-row">
              <div class="form-field">
                <div class="label">4. HOME UNIT NAME & ADDRESS:</div>
                <div class="textarea-value">${ratingToPrint.home_unit_name_address || ''}</div>
              </div>
              <div class="form-field">
                <div class="label">5. INCIDENT AGENCY & ADDRESS:</div>
                <div class="textarea-value">${ratingToPrint.incident_agency_address || ''}</div>
              </div>
            </div>
          </div>

          <div class="form-section">
            <div class="form-row">
              <div class="form-field">
                <div class="label">6. POSITION HELD ON INCIDENT:</div>
                <div class="value">${ratingToPrint.position_held || ''}</div>
              </div>
              <div class="form-field">
                <div class="label">7. INCIDENT DEFINITION:</div>
                <div class="value">${ratingToPrint.incident_definition || ''}</div>
              </div>
            </div>
          </div>

          <div class="form-section">
            <div class="form-row">
              <div class="form-field">
                <div class="label">8. DATE(S) OF ASSIGNMENT:</div>
                <div style="display: flex; gap: 10px;">
                  <div style="flex: 1;">
                    <span style="font-size: 10px;">From: </span>
                    <span style="border-bottom: 1px solid #000; padding: 2px;">${ratingToPrint.date_from || ''}</span>
                  </div>
                  <div style="flex: 1;">
                    <span style="font-size: 10px;">To: </span>
                    <span style="border-bottom: 1px solid #000; padding: 2px;">${ratingToPrint.date_to || ''}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="form-section">
            <div class="form-row">
              <div class="form-field">
                <div class="label">9. INCIDENT COMPLEXITY:</div>
                <div class="complexity-options">
                  ${['Type 1', '2', '3', '4', '5'].map(type => 
                    `<div class="complexity-option">
                       <span>${radioMark(ratingToPrint.incident_complexity === type)}</span>
                       <span>${type}</span>
                     </div>`
                  ).join('')}
                </div>
              </div>
            </div>
          </div>

          <div class="section-title">PERFORMANCE EVALUATION</div>
          
          <table class="rating-table">
            <thead>
              <tr>
                <th style="width: 40%;">RATING FACTORS</th>
                <th>NA</th>
                <th>A<br/>UNACCEPTABLE</th>
                <th>B<br/>MET STANDARDS</th>
                <th>C<br/>MET STANDARDS</th>
                <th>D<br/>EXCEEDED EXPECTATIONS</th>
                <th>E<br/>EXCEEDED EXPECTATIONS</th>
              </tr>
            </thead>
            <tbody>
              ${[
                { key: 'knowledge_job_competence', num: '11', title: 'KNOWLEDGE OF THE JOB/PROFESSIONAL COMPETENCE', desc: 'Ability to acquire, apply, and share technical and administrative knowledge and skills associated with description of duties.' },
                { key: 'ability_obtain_performance', num: '12', title: 'ABILITY TO OBTAIN PERFORMANCE/RESULTS', desc: 'Quality, quantity, timeliness, and impact of work.' },
                { key: 'planning_preparedness', num: '13', title: 'PLANNING/PREPAREDNESS', desc: 'Ability to anticipate, determine goals, identify relevant information, set priorities and deadlines.' },
                { key: 'using_resources', num: '14', title: 'USING RESOURCES', desc: 'Ability to manage time, materials, information, money, and people.' },
                { key: 'adaptability_attitude', num: '15', title: 'ADAPTABILITY/ATTITUDE', desc: 'Ability to maintain a positive attitude and modify work methods in response to changing conditions.' },
                { key: 'communication_skills', num: '16', title: 'COMMUNICATION SKILLS', desc: 'Ability to speak effectively and listen to understand. Express facts and ideas clearly and convincingly.' },
                { key: 'ability_work_team', num: '17', title: 'ABILITY TO WORK ON A TEAM', desc: 'Ability to manage, lead and participate in teams, encourage cooperation, and develop esprit de corps.' },
                { key: 'consideration_personnel_welfare', num: '18', title: 'CONSIDERATION FOR PERSONNEL/TEAM WELFARE', desc: 'Ability to consider and respond to others\' personal needs, capabilities, and achievements.' },
                { key: 'directing_others', num: '19', title: 'DIRECTING OTHERS', desc: 'Ability to influence or direct others in accomplishing tasks or missions.' },
                { key: 'judgment_decisions_stress', num: '20', title: 'JUDGMENT/DECISIONS UNDER STRESS', desc: 'Ability to make sound decisions using facts, experience, and analytical thought.' },
                { key: 'initiative', num: '21', title: 'INITIATIVE', desc: 'Ability to originate and act on new ideas, pursue opportunities to learn and seek responsibility.' },
                { key: 'physical_ability_job', num: '22', title: 'PHYSICAL ABILITY FOR THE JOB', desc: 'Ability to invest in the team\'s future by caring for physical health and emotional well-being.' },
                { key: 'adherence_safety', num: '23', title: 'ADHERENCE TO SAFETY', desc: 'Ability to invest in the team\'s future by caring for the safety of self and others.' }
              ].map(factor => `
                <tr>
                  <td class="rating-factor">
                    <strong>${factor.num}. ${factor.title}</strong>
                    <div class="factor-description">${factor.desc}</div>
                  </td>
                  <td>${radioMark(ratingToPrint[factor.key] === 'na')}</td>
                  <td>${radioMark(ratingToPrint[factor.key] === 'a')}</td>
                  <td>${radioMark(ratingToPrint[factor.key] === 'b')}</td>
                  <td>${radioMark(ratingToPrint[factor.key] === 'c')}</td>
                  <td>${radioMark(ratingToPrint[factor.key] === 'd')}</td>
                  <td>${radioMark(ratingToPrint[factor.key] === 'e')}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>

          <div style="margin: 20px 0;">
            <div class="label">24. REMARKS: (This rating has been discussed with me)</div>
            <div class="textarea-value" style="min-height: 60px;">${ratingToPrint.remarks || ''}</div>
          </div>
          
          <div class="form-section">
            <div class="form-row">
              <div class="form-field">
                <div class="label">25. RATED INDIVIDUAL:</div>
                <div style="margin-top: 5px;">
                  <div style="font-size: 10px;">Name:</div>
                  <div class="value" style="margin-bottom: 10px;">${ratingToPrint.rated_individual_name || ''}</div>
                  <div style="display: flex; gap: 20px;">
                    <div style="flex: 1;">
                      <div style="font-size: 10px;">Signature:</div>
                      <div class="value">${ratingToPrint.rated_individual_signature || ''}</div>
                    </div>
                    <div style="flex: 1;">
                      <div style="font-size: 10px;">Date:</div>
                      <div class="value">${ratingToPrint.rated_individual_date || ''}</div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="form-field">
                <div class="label">26. RATED BY:</div>
                <div style="margin-top: 5px;">
                  <div style="font-size: 10px;">Name:</div>
                  <div class="value" style="margin-bottom: 10px;">${ratingToPrint.rated_by_name || ''}</div>
                  <div style="display: flex; gap: 20px;">
                    <div style="flex: 1;">
                      <div style="font-size: 10px;">Signature:</div>
                      <div class="value">${ratingToPrint.rated_by_signature || ''}</div>
                    </div>
                    <div style="flex: 1;">
                      <div style="font-size: 10px;">Date:</div>
                      <div class="value">${ratingToPrint.rated_by_date || ''}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    writeSanitizedHTML(printWindow.document, printContent);
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  const PerformanceRadioGroup = ({ name, value, onChange }) => {
    const options = [
      { value: 'na', label: 'NA' },
      { value: 'a', label: 'A - Unacceptable' },
      { value: 'b', label: 'B - Met Standards' },
      { value: 'c', label: 'C - Met Standards' },
      { value: 'd', label: 'D - Exceeded Expectations' },
      { value: 'e', label: 'E - Exceeded Expectations' }
    ];

    return (
      <div className="grid grid-cols-6 gap-2">
        {options.map((option) => (
          <label key={option.value} className="flex flex-col items-center gap-1 text-white text-xs">
            <input
              type="radio"
              name={name}
              value={option.value}
              checked={value === option.value}
              onChange={(e) => onChange(e.target.value)}
              className="w-4 h-4 text-orange-500 bg-gray-700 border-gray-600 focus:ring-orange-500"
            />
            <span className="text-center leading-tight">{option.label}</span>
          </label>
        ))}
      </div>
    );
  };

  if (view === 'list') {
    return (
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className={`text-2xl font-bold ${theme.colors.textPrimary} mb-2`}>ICS 225 - Personnel Performance Rating</h2>
            <p className={`${theme.colors.textMuted}`}>
              Evaluate individual personnel performance during incident operations.
            </p>
          </div>
          <Button
            onClick={() => { resetForm(); setView('add'); }}
            className="bg-orange-600 hover:bg-orange-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            New ICS 225
          </Button>
        </div>

        {ratings.length === 0 ? (
          <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <User className={`h-12 w-12 ${theme.colors.textMuted} mb-4`} />
              <p className={`${theme.colors.textMuted} text-lg mb-4`}>No personnel performance ratings yet</p>
              <Button
                onClick={() => { resetForm(); setView('add'); }}
                className="bg-orange-600 hover:bg-orange-700"
              >
                Create First ICS 225
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {ratings.map((rating) => (
              <Card key={rating.id} className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
                <CardContent className="p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className={`font-semibold ${theme.colors.textPrimary}`}>
                        {rating.name || 'Personnel Performance Rating'}
                      </h3>
                      <p className={`text-sm ${theme.colors.textMuted}`}>
                        Position: {rating.position_held || 'Not specified'}
                      </p>
                      <p className={`text-sm ${theme.colors.textMuted}`}>
                        Incident: {rating.incident_name || 'Not specified'}
                      </p>
                      <p className={`text-sm ${theme.colors.textMuted}`}>
                        Created: {new Date(rating.created_at).toLocaleString()}
                      </p>
                    </div>
                    <div className="flex gap-2 ml-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(rating)}
                        className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                      >
                        <Edit className="h-3 w-3 mr-1" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => printICS225(rating)}
                        className="border-green-500/50 text-green-400 hover:bg-green-500/10"
                      >
                        <Printer className="h-3 w-3 mr-1" />
                        Print
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(rating.id)}
                        className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                      >
                        <Trash2 className="h-3 w-3 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="p-6 pb-24">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className={`text-2xl font-bold ${theme.colors.textPrimary} mb-2`}>
            {editingRating ? 'Edit' : 'Create'} ICS 225 - Personnel Performance Rating
          </h2>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => {
              setView('list');
              setEditingRating(null);
              resetForm();
            }}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to List
          </Button>
          <Button
            onClick={() => printICS225()}
            variant="outline"
            className="border-green-500/50 text-green-400 hover:bg-green-500/10"
          >
            <Printer className="h-4 w-4 mr-2" />
            Print ICS 225
          </Button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header Information */}
        <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
          <CardHeader>
            <CardTitle className="text-orange-500">Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-white">1. Name</Label>
                <Select
                  value={formData.name || ''}
                  onValueChange={(value) => {
                    const selected = participants.find(p => {
                      const displayName = (p.name || `${p.firstName || ''} ${p.lastName || ''}`).trim();
                      return displayName === value;
                    });
                    setFormData(prev => ({
                      ...prev,
                      name: value,
                      position_held: selected?.position || prev.position_held
                    }));
                  }}
                >
                  <SelectTrigger className="bg-gray-800 border-gray-700 text-white" data-testid="ics225-name-select">
                    <SelectValue placeholder={participants.length === 0 ? 'No participants assigned to this exercise' : 'Select a participant...'} />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-gray-700">
                    {participants.map((p, idx) => {
                      const displayName = (p.name || `${p.firstName || ''} ${p.lastName || ''}`).trim();
                      if (!displayName) return null;
                      return (
                        <SelectItem
                          key={p.id || `${displayName}-${idx}`}
                          value={displayName}
                          className="text-white focus:bg-gray-700"
                          data-testid={`ics225-name-option-${idx}`}
                        >
                          {displayName}{p.position ? ` — ${p.position}` : ''}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-white">2. Incident Name</Label>
                <Input
                  value={formData.incident_name}
                  onChange={(e) => handleInputChange('incident_name', e.target.value)}
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
              <div>
                <Label className="text-white">3. Incident Number</Label>
                <Input
                  value={formData.incident_number}
                  onChange={(e) => handleInputChange('incident_number', e.target.value)}
                  placeholder="Enter incident number"
                  className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-white">4. Home Unit Name & Address</Label>
                <Textarea
                  value={formData.home_unit_name_address}
                  onChange={(e) => handleInputChange('home_unit_name_address', e.target.value)}
                  placeholder="Enter home unit name and address"
                  className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
                  rows={3}
                />
              </div>
              <div>
                <Label className="text-white">5. Incident Agency & Address</Label>
                <Textarea
                  value={formData.incident_agency_address}
                  onChange={(e) => handleInputChange('incident_agency_address', e.target.value)}
                  placeholder="Enter incident agency and address"
                  className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
                  rows={3}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-white">6. Position Held on Incident</Label>
                <Input
                  value={formData.position_held}
                  onChange={(e) => handleInputChange('position_held', e.target.value)}
                  placeholder="Enter position held"
                  className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
                />
              </div>
              <div>
                <Label className="text-white">7. Incident Definition</Label>
                <Input
                  value={formData.incident_definition}
                  onChange={(e) => handleInputChange('incident_definition', e.target.value)}
                  placeholder="Enter incident definition"
                  className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
                />
              </div>
            </div>

            <div>
              <Label className="text-white">8. Date(s) of Assignment</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-400 text-sm">Date/Time From</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      type="date"
                      value={formData.date_from ? formData.date_from.split('T')[0] : ''}
                      onChange={(e) => {
                        const date = e.target.value;
                        const time = formData.date_from ? formData.date_from.split('T')[1] : '00:00';
                        handleInputChange('date_from', `${date}T${time}`);
                      }}
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                    <Input
                      type="text"
                      placeholder="HH:MM"
                      pattern="[0-2][0-9]:[0-5][0-9]"
                      maxLength="5"
                      value={formData.date_from ? formData.date_from.split('T')[1] || '' : ''}
                      onChange={(e) => {
                        let value = e.target.value.replace(/[^0-9:]/g, '');
                        if (value.length === 2 && !value.includes(':')) {
                          value = value + ':';
                        }
                        if (value.length <= 5) {
                          const date = formData.date_from ? formData.date_from.split('T')[0] : '';
                          handleInputChange('date_from', `${date}T${value}`);
                        }
                      }}
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-gray-400 text-sm">Date/Time To</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      type="date"
                      value={formData.date_to ? formData.date_to.split('T')[0] : ''}
                      onChange={(e) => {
                        const date = e.target.value;
                        const time = formData.date_to ? formData.date_to.split('T')[1] : '00:00';
                        handleInputChange('date_to', `${date}T${time}`);
                      }}
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                    <Input
                      type="text"
                      placeholder="HH:MM"
                      pattern="[0-2][0-9]:[0-5][0-9]"
                      maxLength="5"
                      value={formData.date_to ? formData.date_to.split('T')[1] || '' : ''}
                      onChange={(e) => {
                        let value = e.target.value.replace(/[^0-9:]/g, '');
                        if (value.length === 2 && !value.includes(':')) {
                          value = value + ':';
                        }
                        if (value.length <= 5) {
                          const date = formData.date_to ? formData.date_to.split('T')[0] : '';
                          handleInputChange('date_to', `${date}T${value}`);
                        }
                      }}
                      className="bg-gray-800 border-gray-700 text-white"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div>
              <Label className="text-white">9. Incident Complexity</Label>
              <div className="flex gap-4 mt-2 flex-wrap">
                {['Type 1', '2', '3', '4', '5'].map((type) => (
                  <label key={type} className="flex items-center gap-2 text-white">
                    <input
                      type="radio"
                      name="incident_complexity"
                      value={type}
                      checked={formData.incident_complexity === type}
                      onChange={(e) => handleInputChange('incident_complexity', e.target.value)}
                      className="w-4 h-4 text-orange-500 bg-gray-700 border-gray-600"
                    />
                    <span>{type}</span>
                  </label>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Personnel Evaluation */}
        <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle className="text-orange-500">Personnel Evaluation</CardTitle>
              <div className="text-orange-500 text-sm">
                for definitions, hover over 'Rating Factors','A - Unacceptable','C- Met standards','E-Exceeded Expectations'
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Evaluation Grid */}
            <div className="overflow-x-auto">
              <div className="grid gap-2 min-w-full" style={{gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr'}}>
                {/* Header Row */}
                <div className="font-semibold text-white text-sm p-3 bg-gray-800 rounded-tl">Rating Factors</div>
                <div className="font-semibold text-white text-sm p-3 bg-gray-800 text-center">NA</div>
                <div className="font-semibold text-white text-sm p-3 bg-gray-800 text-center">A<br/>Unacceptable</div>
                <div className="font-semibold text-white text-sm p-3 bg-gray-800 text-center">C<br/>Met Standard</div>
                <div className="font-semibold text-white text-sm p-3 bg-gray-800 text-center rounded-tr">E<br/>Standard Exceeded</div>
                
                {/* Second Row - Knowledge and Professional Competence */}
                <div className="p-3 bg-gray-900/30 border border-gray-700">
                  <div 
                    className="text-white text-sm cursor-help"
                    title="Ability to acquire, apply, and share technical and administrative knowledge and skills associated with description of duties. (Includes operational aspects such as marine safety, seamanship, airmanship, SAR, etc., as appropriate.)"
                  >
                    Professional Competence
                  </div>
                </div>
                
                <div className="p-3 bg-gray-900/30 border border-gray-700 flex justify-center items-center">
                  <input
                    type="radio"
                    name="knowledge_job_competence"
                    value="na"
                    checked={formData.knowledge_job_competence === "na"}
                    onChange={(e) => handleInputChange("knowledge_job_competence", e.target.value)}
                    className="w-4 h-4 text-orange-500 bg-gray-700 border-gray-600 focus:ring-orange-500"
                  />
                </div>
                
                <div className="p-3 bg-gray-900/30 border border-gray-700 flex justify-center items-center">
                  <input
                    type="radio"
                    name="knowledge_job_competence"
                    value="a"
                    checked={formData.knowledge_job_competence === "a"}
                    onChange={(e) => handleInputChange("knowledge_job_competence", e.target.value)}
                    className="w-4 h-4 text-orange-500 bg-gray-700 border-gray-600 focus:ring-orange-500"
                    title="Questionable competence and credibility. Operational or specialty expertise inadequate or lacking in key areas. Made little effort to grow professionally. Used knowledge as power against others or bluffed rather than acknowledging ignorance. Effectiveness reduced due to limited knowledge of own organizational role and customer needs."
                  />
                </div>
                
                <div className="p-3 bg-gray-900/30 border border-gray-700 flex justify-center items-center">
                  <input
                    type="radio"
                    name="knowledge_job_competence"
                    value="c"
                    checked={formData.knowledge_job_competence === "c"}
                    onChange={(e) => handleInputChange("knowledge_job_competence", e.target.value)}
                    className="w-4 h-4 text-orange-500 bg-gray-700 border-gray-600 focus:ring-orange-500"
                    title="Competent and credible authority on specialty or operational issues. Acquired and applied excellent operational or specialty expertise for assigned duties. Showed professional growth through education, training, and professional reading. Shared knowledge and information with others clearly and simply. Understood own organizational role and customer needs."
                  />
                </div>
                
                <div className="p-3 bg-gray-900/30 border border-gray-700 flex justify-center items-center">
                  <input
                    type="radio"
                    name="knowledge_job_competence"
                    value="e"
                    checked={formData.knowledge_job_competence === "e"}
                    onChange={(e) => handleInputChange("knowledge_job_competence", e.target.value)}
                    className="w-4 h-4 text-orange-500 bg-gray-700 border-gray-600 focus:ring-orange-500"
                    title="Superior expertise; advice and actions showed great breadth and depth of knowledge. Remarkable grasp of complex issues, concepts, and situations. Rapidly developed professional growth beyond expectations. Vigorously conveyed knowledge, directly resulting in increased workplace productivity. Insightful knowledge of own role, customer needs, and value of work."
                  />
                </div>
                
                {/* Third Row - Ability to Obtain Performance/Results */}
                <div className="p-3 bg-gray-800/50 border border-gray-700">
                  <div 
                    className="text-white text-sm cursor-help"
                    title="Quality, quantity, timeliness, and impact of work."
                  >
                    Ability to Obtain Results
                  </div>
                </div>
                
                <div className="p-3 bg-gray-800/50 border border-gray-700 flex justify-center items-center">
                  <input
                    type="radio"
                    name="ability_obtain_performance"
                    value="na"
                    checked={formData.ability_obtain_performance === "na"}
                    onChange={(e) => handleInputChange("ability_obtain_performance", e.target.value)}
                    className="w-4 h-4 text-orange-500 bg-gray-700 border-gray-600 focus:ring-orange-500"
                  />
                </div>
                
                <div className="p-3 bg-gray-800/50 border border-gray-700 flex justify-center items-center">
                  <input
                    type="radio"
                    name="ability_obtain_performance"
                    value="a"
                    checked={formData.ability_obtain_performance === "a"}
                    onChange={(e) => handleInputChange("ability_obtain_performance", e.target.value)}
                    className="w-4 h-4 text-orange-500 bg-gray-700 border-gray-600 focus:ring-orange-500"
                    title="Routine tasks accomplished with difficulty. Results often late or of poor quality. Work had a negative impact on department or unit. Maintained the status quo despite opportunities to improve."
                  />
                </div>
                
                <div className="p-3 bg-gray-800/50 border border-gray-700 flex justify-center items-center">
                  <input
                    type="radio"
                    name="ability_obtain_performance"
                    value="c"
                    checked={formData.ability_obtain_performance === "c"}
                    onChange={(e) => handleInputChange("ability_obtain_performance", e.target.value)}
                    className="w-4 h-4 text-orange-500 bg-gray-700 border-gray-600 focus:ring-orange-500"
                    title="Got the job done in all routine situations and in many unusual ones. Work was timely and of high quality; required same of subordinates. Results had a positive impact on IMT. Continuously improved services and organizational effectiveness."
                  />
                </div>
                
                <div className="p-3 bg-gray-800/50 border border-gray-700 flex justify-center items-center">
                  <input
                    type="radio"
                    name="ability_obtain_performance"
                    value="e"
                    checked={formData.ability_obtain_performance === "e"}
                    onChange={(e) => handleInputChange("ability_obtain_performance", e.target.value)}
                    className="w-4 h-4 text-orange-500 bg-gray-700 border-gray-600 focus:ring-orange-500"
                    title="Maintained optimal balance among quality, quantity, and timeliness of work. Quality of own and subordinates' work surpassed expectations. Results had a significant positive impact on the IMT. Established clearly effective systems of continuous improvement."
                  />
                </div>
                
                {/* Fourth Row - Planning / Preparedness */}
                <div className="p-3 bg-gray-900/30 border border-gray-700">
                  <div 
                    className="text-white text-sm cursor-help"
                    title="Ability to anticipate, determine goals, identify relevant information, set priorities and deadlines, and create a shared vision of the Incident Management Team (IMT)."
                  >
                    Planning / Preparedness
                  </div>
                </div>
                
                <div className="p-3 bg-gray-900/30 border border-gray-700 flex justify-center items-center">
                  <input
                    type="radio"
                    name="planning_preparedness"
                    value="na"
                    checked={formData.planning_preparedness === "na"}
                    onChange={(e) => handleInputChange("planning_preparedness", e.target.value)}
                    className="w-4 h-4 text-orange-500 bg-gray-700 border-gray-600 focus:ring-orange-500"
                  />
                </div>
                
                <div className="p-3 bg-gray-900/30 border border-gray-700 flex justify-center items-center">
                  <input
                    type="radio"
                    name="planning_preparedness"
                    value="a"
                    checked={formData.planning_preparedness === "a"}
                    onChange={(e) => handleInputChange("planning_preparedness", e.target.value)}
                    className="w-4 h-4 text-orange-500 bg-gray-700 border-gray-600 focus:ring-orange-500"
                    title="Got caught by the unexpected; appeared to be controlled by events. Set vague or unrealistic goals. Used unreasonable criteria to set priorities and deadlines. Rarely had plan of action. Failed to focus on relevant information."
                  />
                </div>
                
                <div className="p-3 bg-gray-900/30 border border-gray-700 flex justify-center items-center">
                  <input
                    type="radio"
                    name="planning_preparedness"
                    value="c"
                    checked={formData.planning_preparedness === "c"}
                    onChange={(e) => handleInputChange("planning_preparedness", e.target.value)}
                    className="w-4 h-4 text-orange-500 bg-gray-700 border-gray-600 focus:ring-orange-500"
                    title="Consistently prepared. Set high but realistic goals. Used sound criteria to set priorities and deadlines. Used quality tools and processes to develop action plans. Identified key information. Kept supervisors and stakeholders informed."
                  />
                </div>
                
                <div className="p-3 bg-gray-900/30 border border-gray-700 flex justify-center items-center">
                  <input
                    type="radio"
                    name="planning_preparedness"
                    value="e"
                    checked={formData.planning_preparedness === "e"}
                    onChange={(e) => handleInputChange("planning_preparedness", e.target.value)}
                    className="w-4 h-4 text-orange-500 bg-gray-700 border-gray-600 focus:ring-orange-500"
                    title="Exceptional preparation. Always looked beyond immediate events or problems. Skillfully balanced competing demands. Developed strategies with contingency plans. Assessed all aspects of problems, including underlying issues and impact."
                  />
                </div>
                
                {/* Fifth Row - Using Resources */}
                <div className="p-3 bg-gray-800/50 border border-gray-700">
                  <div 
                    className="text-white text-sm cursor-help"
                    title="Ability to manage time, materials, information, money, and people (i.e., all IMT components as well as external publics)."
                  >
                    Using Resources
                  </div>
                </div>
                
                <div className="p-3 bg-gray-800/50 border border-gray-700 flex justify-center items-center">
                  <input
                    type="radio"
                    name="using_resources"
                    value="na"
                    checked={formData.using_resources === "na"}
                    onChange={(e) => handleInputChange("using_resources", e.target.value)}
                    className="w-4 h-4 text-orange-500 bg-gray-700 border-gray-600 focus:ring-orange-500"
                  />
                </div>
                
                <div className="p-3 bg-gray-800/50 border border-gray-700 flex justify-center items-center">
                  <input
                    type="radio"
                    name="using_resources"
                    value="a"
                    checked={formData.using_resources === "a"}
                    onChange={(e) => handleInputChange("using_resources", e.target.value)}
                    className="w-4 h-4 text-orange-500 bg-gray-700 border-gray-600 focus:ring-orange-500"
                    title="Concentrated on unproductive activities or often overlooked critical demands. Failed to use people productively. Did not follow up. Mismanaged information, money, or time. Used ineffective tools or left subordinates without means to accomplish tasks. Employed wasteful methods."
                  />
                </div>
                
                <div className="p-3 bg-gray-800/50 border border-gray-700 flex justify-center items-center">
                  <input
                    type="radio"
                    name="using_resources"
                    value="c"
                    checked={formData.using_resources === "c"}
                    onChange={(e) => handleInputChange("using_resources", e.target.value)}
                    className="w-4 h-4 text-orange-500 bg-gray-700 border-gray-600 focus:ring-orange-500"
                    title="Effectively managed a variety of activities with available resources. Delegated, empowered, and followed up. Skilled time manager, budgeted own and subordinates' time productively. Ensured subordinates had adequate tools, materials, time, and direction. Cost conscious, sought ways to cut waste."
                  />
                </div>
                
                <div className="p-3 bg-gray-800/50 border border-gray-700 flex justify-center items-center">
                  <input
                    type="radio"
                    name="using_resources"
                    value="e"
                    checked={formData.using_resources === "e"}
                    onChange={(e) => handleInputChange("using_resources", e.target.value)}
                    className="w-4 h-4 text-orange-500 bg-gray-700 border-gray-600 focus:ring-orange-500"
                    title="Unusually skilled at bringing scarce resources to bear on the most critical of competing demands. Optimized productivity through effective delegation, empowerment, and follow-up control. Found ways to systematically reduce cost, eliminate waste, and improve efficiency."
                  />
                </div>
                
                {/* Sixth Row - Adaptability / Attitude */}
                <div className="p-3 bg-gray-900/30 border border-gray-700">
                  <div 
                    className="text-white text-sm cursor-help"
                    title="Ability to maintain a positive attitude and modify work methods and priorities in response to new information, changing conditions, political realities, or unexpected obstacles."
                  >
                    Adaptability / Attitude
                  </div>
                </div>
                
                <div className="p-3 bg-gray-900/30 border border-gray-700 flex justify-center items-center">
                  <input
                    type="radio"
                    name="adaptability_attitude"
                    value="na"
                    checked={formData.adaptability_attitude === "na"}
                    onChange={(e) => handleInputChange("adaptability_attitude", e.target.value)}
                    className="w-4 h-4 text-orange-500 bg-gray-700 border-gray-600 focus:ring-orange-500"
                  />
                </div>
                
                <div className="p-3 bg-gray-900/30 border border-gray-700 flex justify-center items-center">
                  <input
                    type="radio"
                    name="adaptability_attitude"
                    value="a"
                    checked={formData.adaptability_attitude === "a"}
                    onChange={(e) => handleInputChange("adaptability_attitude", e.target.value)}
                    className="w-4 h-4 text-orange-500 bg-gray-700 border-gray-600 focus:ring-orange-500"
                    title="Unable to gauge effectiveness of work, recognize political realities, or make adjustments when needed. Maintained a poor outlook. Overlooked or screened out new information. Ineffective in ambiguous, complex, or pressured situations."
                  />
                </div>
                
                <div className="p-3 bg-gray-900/30 border border-gray-700 flex justify-center items-center">
                  <input
                    type="radio"
                    name="adaptability_attitude"
                    value="c"
                    checked={formData.adaptability_attitude === "c"}
                    onChange={(e) => handleInputChange("adaptability_attitude", e.target.value)}
                    className="w-4 h-4 text-orange-500 bg-gray-700 border-gray-600 focus:ring-orange-500"
                    title="Receptive to change, new information, and technology. Effectively used benchmarks to improve performance and service. Monitored progress and changed course as required. Maintained a positive approach. Effectively dealt with pressure and ambiguity. Facilitated smooth transitions. Adjusted direction to accommodate political realities."
                  />
                </div>
                
                <div className="p-3 bg-gray-900/30 border border-gray-700 flex justify-center items-center">
                  <input
                    type="radio"
                    name="adaptability_attitude"
                    value="e"
                    checked={formData.adaptability_attitude === "e"}
                    onChange={(e) => handleInputChange("adaptability_attitude", e.target.value)}
                    className="w-4 h-4 text-orange-500 bg-gray-700 border-gray-600 focus:ring-orange-500"
                    title="Rapidly assessed and confidently adjusted to changing conditions, political realities, new information, and technology. Very skilled at using and responding to measurement indicators. Championed organizational improvements. Effectively dealt with extremely complex situations. Turned pressure and ambiguity into constructive forces for change."
                  />
                </div>
                
                {/* Seventh Row - Communication Skills */}
                <div className="p-3 bg-gray-800/50 border border-gray-700">
                  <div 
                    className="text-white text-sm cursor-help"
                    title="Ability to speak effectively and listen to understand. Ability to express facts and ideas clearly and convincingly."
                  >
                    Communication Skills
                  </div>
                </div>
                
                <div className="p-3 bg-gray-800/50 border border-gray-700 flex justify-center items-center">
                  <input
                    type="radio"
                    name="communication_skills"
                    value="na"
                    checked={formData.communication_skills === "na"}
                    onChange={(e) => handleInputChange("communication_skills", e.target.value)}
                    className="w-4 h-4 text-orange-500 bg-gray-700 border-gray-600 focus:ring-orange-500"
                  />
                </div>
                
                <div className="p-3 bg-gray-800/50 border border-gray-700 flex justify-center items-center">
                  <input
                    type="radio"
                    name="communication_skills"
                    value="a"
                    checked={formData.communication_skills === "a"}
                    onChange={(e) => handleInputChange("communication_skills", e.target.value)}
                    className="w-4 h-4 text-orange-500 bg-gray-700 border-gray-600 focus:ring-orange-500"
                    title="Unable to effectively articulate ideas and facts; lacked preparation, confidence, or logic. Used inappropriate language or rambled. Nervous or distracting mannerisms detracted from message. Failed to listen carefully or was too argumentative. Written material frequently unclear, verbose, or poorly organized. Seldom proofread."
                  />
                </div>
                
                <div className="p-3 bg-gray-800/50 border border-gray-700 flex justify-center items-center">
                  <input
                    type="radio"
                    name="communication_skills"
                    value="c"
                    checked={formData.communication_skills === "c"}
                    onChange={(e) => handleInputChange("communication_skills", e.target.value)}
                    className="w-4 h-4 text-orange-500 bg-gray-700 border-gray-600 focus:ring-orange-500"
                    title="Effectively expressed ideas and facts in individual and group situations; nonverbal actions consistent with spoken message. Communicated to people at all levels to ensure understanding. Listened carefully for intended message as well as spoken words. Written material clear, concise, and logically organized. Proofread conscientiously."
                  />
                </div>
                
                <div className="p-3 bg-gray-800/50 border border-gray-700 flex justify-center items-center">
                  <input
                    type="radio"
                    name="communication_skills"
                    value="e"
                    checked={formData.communication_skills === "e"}
                    onChange={(e) => handleInputChange("communication_skills", e.target.value)}
                    className="w-4 h-4 text-orange-500 bg-gray-700 border-gray-600 focus:ring-orange-500"
                    title="Clearly articulated and promoted ideas before a wide range of audiences; accomplished speaker in both formal and extemporaneous situations. Adept at presenting complex or sensitive issues. Active listener; remarkable ability to listen with open mind and identify key issues. Clearly and persuasively expressed complex or controversial material, directly contributing to stated objectives."
                  />
                </div>
                
                {/* Eighth Row - Ability to Work on a Team */}
                <div className="p-3 bg-gray-900/30 border border-gray-700">
                  <div 
                    className="text-white text-sm cursor-help"
                    title="Ability to manage, lead and participate in teams, encourage cooperation, and develop esprit de corps."
                  >
                    Ability to Work on a Team
                  </div>
                </div>
                
                <div className="p-3 bg-gray-900/30 border border-gray-700 flex justify-center items-center">
                  <input
                    type="radio"
                    name="ability_work_team"
                    value="na"
                    checked={formData.ability_work_team === "na"}
                    onChange={(e) => handleInputChange("ability_work_team", e.target.value)}
                    className="w-4 h-4 text-orange-500 bg-gray-700 border-gray-600 focus:ring-orange-500"
                  />
                </div>
                
                <div className="p-3 bg-gray-900/30 border border-gray-700 flex justify-center items-center">
                  <input
                    type="radio"
                    name="ability_work_team"
                    value="a"
                    checked={formData.ability_work_team === "a"}
                    onChange={(e) => handleInputChange("ability_work_team", e.target.value)}
                    className="w-4 h-4 text-orange-500 bg-gray-700 border-gray-600 focus:ring-orange-500"
                    title="Used teams ineffectively or at wrong times. Conflicts mismanaged or often left unresolved, resulting in decreased team effectiveness. Excluded team members from vital information. Stifled group discussions or did not contribute productively. Inhibited cross functional cooperation to the detriment of unit or service goals."
                  />
                </div>
                
                <div className="p-3 bg-gray-900/30 border border-gray-700 flex justify-center items-center">
                  <input
                    type="radio"
                    name="ability_work_team"
                    value="c"
                    checked={formData.ability_work_team === "c"}
                    onChange={(e) => handleInputChange("ability_work_team", e.target.value)}
                    className="w-4 h-4 text-orange-500 bg-gray-700 border-gray-600 focus:ring-orange-500"
                    title="Skillfully used teams to increase unit effectiveness, quality, and service. Resolved or managed group conflict, enhanced cooperation, and involved team members in decision process. Valued team participation. Effectively negotiated work across functional boundaries to enhance support of broader mutual goals"
                  />
                </div>
                
                <div className="p-3 bg-gray-900/30 border border-gray-700 flex justify-center items-center">
                  <input
                    type="radio"
                    name="ability_work_team"
                    value="e"
                    checked={formData.ability_work_team === "e"}
                    onChange={(e) => handleInputChange("ability_work_team", e.target.value)}
                    className="w-4 h-4 text-orange-500 bg-gray-700 border-gray-600 focus:ring-orange-500"
                    title="Insightful use of teams raised unit productivity beyond expectations. Inspired high level of esprit de corps, even in difficult situations. Major contributor to team effort. Established relationships and networks across a broad range of people and groups, raising accomplishments of mutual goals to a remarkable level."
                  />
                </div>
                
                {/* Ninth Row - Consideration for Personnel/Team Welfare */}
                <div className="p-3 bg-gray-800/50 border border-gray-700">
                  <div 
                    className="text-white text-sm cursor-help"
                    title="Ability to consider and respond to others' personal needs, capabilities, and achievements; support for and application of work life concepts and skills."
                  >
                    Consideration for Personnel/Team Welfare
                  </div>
                </div>
                
                <div className="p-3 bg-gray-800/50 border border-gray-700 flex justify-center items-center">
                  <input
                    type="radio"
                    name="consideration_personnel_welfare"
                    value="na"
                    checked={formData.consideration_personnel_welfare === "na"}
                    onChange={(e) => handleInputChange("consideration_personnel_welfare", e.target.value)}
                    className="w-4 h-4 text-orange-500 bg-gray-700 border-gray-600 focus:ring-orange-500"
                  />
                </div>
                
                <div className="p-3 bg-gray-800/50 border border-gray-700 flex justify-center items-center">
                  <input
                    type="radio"
                    name="consideration_personnel_welfare"
                    value="a"
                    checked={formData.consideration_personnel_welfare === "a"}
                    onChange={(e) => handleInputChange("consideration_personnel_welfare", e.target.value)}
                    className="w-4 h-4 text-orange-500 bg-gray-700 border-gray-600 focus:ring-orange-500"
                    title="Seldom recognized or responded to needs of people; left outside resources untapped despite apparent need. Ignorance of individuals' capabilities increased chance of failure. Seldom recognized or rewarded deserving subordinates or other IMT members."
                  />
                </div>
                
                <div className="p-3 bg-gray-800/50 border border-gray-700 flex justify-center items-center">
                  <input
                    type="radio"
                    name="consideration_personnel_welfare"
                    value="c"
                    checked={formData.consideration_personnel_welfare === "c"}
                    onChange={(e) => handleInputChange("consideration_personnel_welfare", e.target.value)}
                    className="w-4 h-4 text-orange-500 bg-gray-700 border-gray-600 focus:ring-orange-500"
                    title="Cared for people. Recognized and responded to their needs; referred to outside resources as appropriate. Considered individuals' capabilities to maximize opportunities for success. Consistently recognized and rewarded deserving subordinates or other IMT members."
                  />
                </div>
                
                <div className="p-3 bg-gray-800/50 border border-gray-700 flex justify-center items-center">
                  <input
                    type="radio"
                    name="consideration_personnel_welfare"
                    value="e"
                    checked={formData.consideration_personnel_welfare === "e"}
                    onChange={(e) => handleInputChange("consideration_personnel_welfare", e.target.value)}
                    className="w-4 h-4 text-orange-500 bg-gray-700 border-gray-600 focus:ring-orange-500"
                    title="Always accessible. Enhanced overall quality of life. Actively contributed to achieving balance among IMT requirements and professional and personal responsibilities. Strong advocate for subordinates; ensured appropriate and timely recognition, both formal and informal."
                  />
                </div>
                
                {/* Tenth Row - Directing Others */}
                <div className="p-3 bg-gray-900/30 border border-gray-700">
                  <div 
                    className="text-white text-sm cursor-help"
                    title="Ability to influence or direct others in accomplishing tasks or missions."
                  >
                    Directing Others
                  </div>
                </div>
                
                <div className="p-3 bg-gray-900/30 border border-gray-700 flex justify-center items-center">
                  <input
                    type="radio"
                    name="directing_others"
                    value="na"
                    checked={formData.directing_others === "na"}
                    onChange={(e) => handleInputChange("directing_others", e.target.value)}
                    className="w-4 h-4 text-orange-500 bg-gray-700 border-gray-600 focus:ring-orange-500"
                  />
                </div>
                
                <div className="p-3 bg-gray-900/30 border border-gray-700 flex justify-center items-center">
                  <input
                    type="radio"
                    name="directing_others"
                    value="a"
                    checked={formData.directing_others === "a"}
                    onChange={(e) => handleInputChange("directing_others", e.target.value)}
                    className="w-4 h-4 text-orange-500 bg-gray-700 border-gray-600 focus:ring-orange-500"
                    title="Showed difficulty in directing or influencing others. Low or unclear work standards reduced productivity. Failed to hold subordinates accountable for shoddy work or irresponsible actions. Unwilling to delegate authority to increase efficiency of task accomplishment."
                  />
                </div>
                
                <div className="p-3 bg-gray-900/30 border border-gray-700 flex justify-center items-center">
                  <input
                    type="radio"
                    name="directing_others"
                    value="c"
                    checked={formData.directing_others === "c"}
                    onChange={(e) => handleInputChange("directing_others", e.target.value)}
                    className="w-4 h-4 text-orange-500 bg-gray-700 border-gray-600 focus:ring-orange-500"
                    title="A leader who earned others' support and commitment. Set high work standards; clearly articulated job requirements, expectations, and measurement criteria; held subordinates accountable. When appropriate, delegated authority to those directly responsible for the task."
                  />
                </div>
                
                <div className="p-3 bg-gray-900/30 border border-gray-700 flex justify-center items-center">
                  <input
                    type="radio"
                    name="directing_others"
                    value="e"
                    checked={formData.directing_others === "e"}
                    onChange={(e) => handleInputChange("directing_others", e.target.value)}
                    className="w-4 h-4 text-orange-500 bg-gray-700 border-gray-600 focus:ring-orange-500"
                    title="An inspirational leader who motivated others to achieve results not normally attainable. Won people over rather than imposing will. Clearly articulated vision; empowered subordinates to set goals and objectives to accomplish tasks. Modified leadership style to best meet challenging situations."
                  />
                </div>
                
                {/* Eleventh Row - Judgement / Decisions Under Stress */}
                <div className="p-3 bg-gray-800/50 border border-gray-700">
                  <div 
                    className="text-white text-sm cursor-help"
                    title="Ability to make sound decisions and provide valid recommendations by using facts, experience, political acumen, common sense, risk assessment, and analytical thought."
                  >
                    Judgement / Decisions Under Stress
                  </div>
                </div>
                
                <div className="p-3 bg-gray-800/50 border border-gray-700 flex justify-center items-center">
                  <input
                    type="radio"
                    name="judgment_decisions_stress"
                    value="na"
                    checked={formData.judgment_decisions_stress === "na"}
                    onChange={(e) => handleInputChange("judgment_decisions_stress", e.target.value)}
                    className="w-4 h-4 text-orange-500 bg-gray-700 border-gray-600 focus:ring-orange-500"
                  />
                </div>
                
                <div className="p-3 bg-gray-800/50 border border-gray-700 flex justify-center items-center">
                  <input
                    type="radio"
                    name="judgment_decisions_stress"
                    value="a"
                    checked={formData.judgment_decisions_stress === "a"}
                    onChange={(e) => handleInputChange("judgment_decisions_stress", e.target.value)}
                    className="w-4 h-4 text-orange-500 bg-gray-700 border-gray-600 focus:ring-orange-500"
                    title="Decisions often displayed poor analysis. Failed to make necessary decisions, or jumped to conclusions without considering facts, alternatives, and impact. Did not effectively weigh risk, cost, and time considerations. Unconcerned with political drivers on organization."
                  />
                </div>
                
                <div className="p-3 bg-gray-800/50 border border-gray-700 flex justify-center items-center">
                  <input
                    type="radio"
                    name="judgment_decisions_stress"
                    value="c"
                    checked={formData.judgment_decisions_stress === "c"}
                    onChange={(e) => handleInputChange("judgment_decisions_stress", e.target.value)}
                    className="w-4 h-4 text-orange-500 bg-gray-700 border-gray-600 focus:ring-orange-500"
                    title="Demonstrated analytical thought and common sense in making decisions. Used facts, data, and experience, and considered the impact of alternatives and political realities. Weighed risk, cost, and time considerations. Made sound decisions promptly with the best available information."
                  />
                </div>
                
                <div className="p-3 bg-gray-800/50 border border-gray-700 flex justify-center items-center">
                  <input
                    type="radio"
                    name="judgment_decisions_stress"
                    value="e"
                    checked={formData.judgment_decisions_stress === "e"}
                    onChange={(e) => handleInputChange("judgment_decisions_stress", e.target.value)}
                    className="w-4 h-4 text-orange-500 bg-gray-700 border-gray-600 focus:ring-orange-500"
                    title="Combined keen analytical thought, an understanding of political processes, and insight to make appropriate decisions. Focused on the key issues and the most relevant information. Did the right thing at the right time. Actions indicated awareness of impact of decisions on others. Not afraid to take reasonable risks to achieve positive results."
                  />
                </div>
                
                {/* Twelfth Row - Initiative */}
                <div className="p-3 bg-gray-900/30 border border-gray-700">
                  <div 
                    className="text-white text-sm cursor-help"
                    title="Ability to originate and act on new ideas, pursue opportunities to learn and develop, and seek responsibility without guidance and supervision."
                  >
                    Initiative
                  </div>
                </div>
                
                <div className="p-3 bg-gray-900/30 border border-gray-700 flex justify-center items-center">
                  <input
                    type="radio"
                    name="initiative"
                    value="na"
                    checked={formData.initiative === "na"}
                    onChange={(e) => handleInputChange("initiative", e.target.value)}
                    className="w-4 h-4 text-orange-500 bg-gray-700 border-gray-600 focus:ring-orange-500"
                  />
                </div>
                
                <div className="p-3 bg-gray-900/30 border border-gray-700 flex justify-center items-center">
                  <input
                    type="radio"
                    name="initiative"
                    value="a"
                    checked={formData.initiative === "a"}
                    onChange={(e) => handleInputChange("initiative", e.target.value)}
                    className="w-4 h-4 text-orange-500 bg-gray-700 border-gray-600 focus:ring-orange-500"
                    title="Postponed needed action. Implemented or supported improvements only when directed to do so. Showed little interest in career development. Feasible improvements in methods, services, or products went unexplored."
                  />
                </div>
                
                <div className="p-3 bg-gray-900/30 border border-gray-700 flex justify-center items-center">
                  <input
                    type="radio"
                    name="initiative"
                    value="c"
                    checked={formData.initiative === "c"}
                    onChange={(e) => handleInputChange("initiative", e.target.value)}
                    className="w-4 h-4 text-orange-500 bg-gray-700 border-gray-600 focus:ring-orange-500"
                    title="Championed improvement through new ideas, methods, and practices. Anticipated problems and took prompt action to avoid or resolve them. Pursued productivity gains and enhanced mission performance by applying new ideas and methods."
                  />
                </div>
                
                <div className="p-3 bg-gray-900/30 border border-gray-700 flex justify-center items-center">
                  <input
                    type="radio"
                    name="initiative"
                    value="e"
                    checked={formData.initiative === "e"}
                    onChange={(e) => handleInputChange("initiative", e.target.value)}
                    className="w-4 h-4 text-orange-500 bg-gray-700 border-gray-600 focus:ring-orange-500"
                    title="Aggressively sought out additional responsibility. A self-learner. Made worthwhile ideas and practices work when others might have given up. Extremely innovative. Optimized use of new ideas and methods to improve work processes and decision making."
                  />
                </div>
                
                {/* Thirteenth Row - Physical Ability for the Job */}
                <div className="p-3 bg-gray-800/50 border border-gray-700">
                  <div 
                    className="text-white text-sm cursor-help"
                    title="Ability to invest in the IMT's future by caring for the physical health and emotional well-being of self and others."
                  >
                    Physical Ability for the Job
                  </div>
                </div>
                
                <div className="p-3 bg-gray-800/50 border border-gray-700 flex justify-center items-center">
                  <input
                    type="radio"
                    name="physical_ability_job"
                    value="na"
                    checked={formData.physical_ability_job === "na"}
                    onChange={(e) => handleInputChange("physical_ability_job", e.target.value)}
                    className="w-4 h-4 text-orange-500 bg-gray-700 border-gray-600 focus:ring-orange-500"
                  />
                </div>
                
                <div className="p-3 bg-gray-800/50 border border-gray-700 flex justify-center items-center">
                  <input
                    type="radio"
                    name="physical_ability_job"
                    value="a"
                    checked={formData.physical_ability_job === "a"}
                    onChange={(e) => handleInputChange("physical_ability_job", e.target.value)}
                    className="w-4 h-4 text-orange-500 bg-gray-700 border-gray-600 focus:ring-orange-500"
                    title="Failed to meet minimum standards of sobriety. Tolerated or condoned others' alcohol abuse. Seldom considered subordinates' health and well-being. Unwilling or unable to recognize and manage stress despite apparent need."
                  />
                </div>
                
                <div className="p-3 bg-gray-800/50 border border-gray-700 flex justify-center items-center">
                  <input
                    type="radio"
                    name="physical_ability_job"
                    value="c"
                    checked={formData.physical_ability_job === "c"}
                    onChange={(e) => handleInputChange("physical_ability_job", e.target.value)}
                    className="w-4 h-4 text-orange-500 bg-gray-700 border-gray-600 focus:ring-orange-500"
                    title="Committed to health and well-being of self and subordinates. Enhanced personal performance through activities supporting physical and emotional wellbeing. Recognized and managed stress effectively."
                  />
                </div>
                
                <div className="p-3 bg-gray-800/50 border border-gray-700 flex justify-center items-center">
                  <input
                    type="radio"
                    name="physical_ability_job"
                    value="e"
                    checked={formData.physical_ability_job === "e"}
                    onChange={(e) => handleInputChange("physical_ability_job", e.target.value)}
                    className="w-4 h-4 text-orange-500 bg-gray-700 border-gray-600 focus:ring-orange-500"
                    title="Remarkable vitality, enthusiasm, alertness, and energy. Consistently contributed at high levels of activity. Optimized personal performance through involvement in activities that supported physical and emotional well-being. Monitored and helped others deal with stress and enhance health and well-being."
                  />
                </div>
                
                {/* Fourteenth Row - Adherence to Safety */}
                <div className="p-3 bg-gray-900/30 border border-gray-700">
                  <div 
                    className="text-white text-sm cursor-help"
                    title="Ability to invest in the IMT's future by caring for the safety of self and others."
                  >
                    Adherence to Safety
                  </div>
                </div>
                
                <div className="p-3 bg-gray-900/30 border border-gray-700 flex justify-center items-center">
                  <input
                    type="radio"
                    name="adherence_safety"
                    value="na"
                    checked={formData.adherence_safety === "na"}
                    onChange={(e) => handleInputChange("adherence_safety", e.target.value)}
                    className="w-4 h-4 text-orange-500 bg-gray-700 border-gray-600 focus:ring-orange-500"
                  />
                </div>
                
                <div className="p-3 bg-gray-900/30 border border-gray-700 flex justify-center items-center">
                  <input
                    type="radio"
                    name="adherence_safety"
                    value="a"
                    checked={formData.adherence_safety === "a"}
                    onChange={(e) => handleInputChange("adherence_safety", e.target.value)}
                    className="w-4 h-4 text-orange-500 bg-gray-700 border-gray-600 focus:ring-orange-500"
                    title="Failed to adequately identify and protect personnel from safety hazards."
                  />
                </div>
                
                <div className="p-3 bg-gray-900/30 border border-gray-700 flex justify-center items-center">
                  <input
                    type="radio"
                    name="adherence_safety"
                    value="c"
                    checked={formData.adherence_safety === "c"}
                    onChange={(e) => handleInputChange("adherence_safety", e.target.value)}
                    className="w-4 h-4 text-orange-500 bg-gray-700 border-gray-600 focus:ring-orange-500"
                    title="Ensured that safe operating procedures were followed."
                  />
                </div>
                
                <div className="p-3 bg-gray-900/30 border border-gray-700 flex justify-center items-center">
                  <input
                    type="radio"
                    name="adherence_safety"
                    value="e"
                    checked={formData.adherence_safety === "e"}
                    onChange={(e) => handleInputChange("adherence_safety", e.target.value)}
                    className="w-4 h-4 text-orange-500 bg-gray-700 border-gray-600 focus:ring-orange-500"
                    title="Demonstrated a significant commitment toward safety of personnel."
                  />
                </div>
              </div>
            </div>

            {/* Remarks Section */}
            <div className="mt-6">
              <Label className="text-white">Remarks</Label>
              <Textarea
                value={formData.remarks}
                onChange={(e) => handleInputChange('remarks', e.target.value)}
                placeholder="Enter remarks..."
                className="bg-gray-800 border-gray-700 text-white placeholder-gray-400 mt-2"
                rows={4}
              />
            </div>

            {/* Signatures */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6 pt-6 border-t border-gray-700">
              <div>
                <Label className="text-white font-semibold">Rated Individual (This has been discussed with me)</Label>
                <div className="space-y-3 mt-3">
                  <div>
                    <Label className="text-gray-400 text-sm">Name</Label>
                    <Input
                      value={formData.rated_individual_name}
                      onChange={(e) => handleInputChange('rated_individual_name', e.target.value)}
                      placeholder="Enter individual's name"
                      className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-400 text-sm">Signature</Label>
                    <Input
                      value={formData.rated_individual_signature}
                      onChange={(e) => handleInputChange('rated_individual_signature', e.target.value)}
                      placeholder="Digital signature or name"
                      className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-400 text-sm">Date</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        type="date"
                        value={formData.rated_individual_date ? formData.rated_individual_date.split('T')[0] : ''}
                        onChange={(e) => {
                          const date = e.target.value;
                          const time = formData.rated_individual_date ? formData.rated_individual_date.split('T')[1] : '00:00';
                          handleInputChange('rated_individual_date', `${date}T${time}`);
                        }}
                        className="bg-gray-800 border-gray-700 text-white"
                      />
                      <Input
                        type="text"
                        placeholder="HH:MM"
                        pattern="[0-2][0-9]:[0-5][0-9]"
                        maxLength="5"
                        value={formData.rated_individual_date ? formData.rated_individual_date.split('T')[1] : ''}
                        onChange={(e) => {
                          let value = e.target.value.replace(/[^0-9:]/g, '');
                          if (value.length === 2 && !value.includes(':')) {
                            value = value + ':';
                          }
                          if (value.length <= 5) {
                            const date = formData.rated_individual_date ? formData.rated_individual_date.split('T')[0] : '';
                            handleInputChange('rated_individual_date', `${date}T${value}`);
                          }
                        }}
                        className="bg-gray-800 border-gray-700 text-white"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <Label className="text-white font-semibold">Rated BY</Label>
                <div className="space-y-3 mt-3">
                  <div>
                    <Label className="text-gray-400 text-sm">Name</Label>
                    <Input
                      value={formData.rated_by_name}
                      onChange={(e) => handleInputChange('rated_by_name', e.target.value)}
                      placeholder="Enter rater's name"
                      className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-400 text-sm">Signature</Label>
                    <Input
                      value={formData.rated_by_signature}
                      onChange={(e) => handleInputChange('rated_by_signature', e.target.value)}
                      placeholder="Digital signature or name"
                      className="bg-gray-800 border-gray-700 text-white placeholder-gray-400"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-400 text-sm">Date</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        type="date"
                        value={formData.rated_by_date ? formData.rated_by_date.split('T')[0] : ''}
                        onChange={(e) => {
                          const date = e.target.value;
                          const time = formData.rated_by_date ? formData.rated_by_date.split('T')[1] : '00:00';
                          handleInputChange('rated_by_date', `${date}T${time}`);
                        }}
                        className="bg-gray-800 border-gray-700 text-white"
                      />
                      <Input
                        type="text"
                        placeholder="HH:MM"
                        pattern="[0-2][0-9]:[0-5][0-9]"
                        maxLength="5"
                        value={formData.rated_by_date ? formData.rated_by_date.split('T')[1] : ''}
                        onChange={(e) => {
                          let value = e.target.value.replace(/[^0-9:]/g, '');
                          if (value.length === 2 && !value.includes(':')) {
                            value = value + ':';
                          }
                          if (value.length <= 5) {
                            const date = formData.rated_by_date ? formData.rated_by_date.split('T')[0] : '';
                            handleInputChange('rated_by_date', `${date}T${value}`);
                          }
                        }}
                        className="bg-gray-800 border-gray-700 text-white"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Save Button Section */}
        <div className="flex justify-end gap-2 mt-6 sticky bottom-0 bg-gray-900/95 p-4 rounded-lg border border-gray-700">
          <Button
            variant="outline"
            onClick={() => {
              setView('list');
              setEditingRating(null);
              resetForm();
            }}
            data-testid="cancel-ics-225-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            className="bg-orange-600 hover:bg-orange-700"
            data-testid="save-ics-225-btn"
          >
            <Save className="h-4 w-4 mr-2" />
            {editingRating ? 'Update' : 'Save'} ICS 225
          </Button>
        </div>
      </div>
    </div>
  );
};

// ICS 230 - Daily Meeting Schedule Form

export default ICS225Form;
